import ResellerDashboard from '../ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { Avatar, Button, Card, Tabs } from 'antd';
import { Typography } from 'antd';
import './ResellerCustomDomain.scss';
import CustomDomainForm from './CustomDomainForm';
import { BookOpenIcon, GlobeIcon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateTutorialStepStatus } from '@/api/reseller';
import { useState } from 'react';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import TutorialsModal from '../TutorialsModal';
import type { TabsProps } from 'antd';
import Embed from './Embed';

const { Title } = Typography;

const tutorialItems = [
	{
		icon: (
			<div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
				<GlobeIcon className="text-blue-500" size={20} />
			</div>
		),
		title: 'Domain Integration',
		description: 'Add Your Custom Domain to AI Front Desk',
		url: 'https://www.myaifrontdesk.com/reseller-tutorials/add-your-custom-domain-to-ai-front-desk',
	},
];

const ResellerCustomDomain = () => {
	const queryClient = useQueryClient();
	const resellerData = useAppSelector(getResellerData);

	const [isTutorialsModalOpen, setIsTutorialsModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState('1');

	const updateTutorialMutation = useMutation({
		mutationFn: updateTutorialStepStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const handleTutorialClick = () => {
		if (!resellerData.tutorial_steps?.custom_domain_tutorial) {
			updateTutorialMutation.mutate('custom_domain_tutorial');
		}
		setIsTutorialsModalOpen(true);
	};

	const items: TabsProps['items'] = [
		{
			key: '1',
			label: (
				<div
					className={`flex items-center gap-2 px-4 py-2 rounded ${
						activeTab === '1' ? 'bg-blue-600 text-white' : 'bg-white text-black'
					}`}
				>
					<Avatar
						size={24}
						style={{
							backgroundColor: activeTab === '1' ? '#fff' : '#1677ff',
							color: activeTab === '1' ? '#1677ff' : '#fff',
							fontSize: 14,
						}}
					>
						1
					</Avatar>
					<span>Domain Integration</span>
				</div>
			),
			children: (
				<div className="mt-5">
					<CustomDomainForm />
				</div>
			),
		},
		{
			key: '2',
			label: (
				<div
					className={`flex items-center gap-2 px-3 py-2 rounded ${
						activeTab === '2' ? 'bg-blue-600 text-white' : 'bg-white text-black'
					}`}
				>
					<Avatar
						size={24}
						style={{
							backgroundColor: activeTab === '2' ? '#fff' : '#1677ff',
							color: activeTab === '2' ? '#1677ff' : '#fff',
							fontSize: 14,
						}}
					>
						2
					</Avatar>
					<span>Iframe</span>
				</div>
			),
			children: (
				<div className="mt-5">
					<Embed resellerData={resellerData} />
				</div>
			),
		},
	];

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.CUSTOM_DOMAIN}>
			<div className="p-6 mb-4">
				<Card className="">
					<div className="flex items-center justify-between">
						<Title level={3}>Enable Client Login and Registration Directly on Your Website</Title>
						<Button
							type="default"
							onClick={handleTutorialClick}
							className={
								!resellerData.tutorial_steps?.custom_domain_tutorial
									? 'bg-[#F1F8FF] hover:bg-[#F1F8FF] text-blue-600 has-ping overflow-hidden'
									: ''
							}
						>
							<BookOpenIcon size={16} />
							<span>Tutorials</span>
						</Button>
					</div>
					<p className="mt-4">
						We offer two seamless options for embedding login and registration pages on your
						website: a custom domain integration or a simple iframe. If you choose to use your own
						domain, you'll need to update your DNS records accordingly. Follow the instructions
						below, or refer to the tutorial on the right for step-by-step guidance. The login page
						will be automatically created in the form of yourdomain/login.
					</p>

					<Title level={5}>Choose one of the 2 options below.</Title>
					<Tabs size="middle" defaultActiveKey="1" items={items} onChange={setActiveTab} />
				</Card>
			</div>

			<TutorialsModal
				isOpen={isTutorialsModalOpen}
				onClose={() => setIsTutorialsModalOpen(false)}
				tutorialItems={tutorialItems}
			/>
		</ResellerDashboard>
	);
};

export default ResellerCustomDomain;
